import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useMeCore } from '../../../../../core/modules/me';
import {
  inventoryCountService,
  InventoryCountSession,
} from '../../../../../core/modules/inventoryCount/services/api';

/**
 * Primera pantalla que ve un tomador de inventario al entrar: si hay una
 * (o varias) sesiones activas a las que tiene acceso, deja elegir a cual
 * sumarse; si no hay ninguna, muestra un estado vacio.
 */
const ActiveSessionScreen = () => {
  const navigate = useNavigate();
  const { companies, getMeCompanies } = useMeCore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [sessions, setSessions] = useState<InventoryCountSession[]>([]);

  useEffect(() => {
    getMeCompanies();
  }, [getMeCompanies]);

  const companyOptions = useMemo(() => companies || [], [companies]);

  useEffect(() => {
    if (companyOptions.length === 1) {
      setSelectedCompanyId(Number(companyOptions[0].id));
    }
  }, [companyOptions]);

  useEffect(() => {
    if (!selectedCompanyId) {
      if (companyOptions.length > 0) {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setError(null);
    inventoryCountService
      .listActiveSessions(selectedCompanyId)
      .then(setSessions)
      .catch(() => setError('No se pudo consultar si hay una toma de inventario activa.'))
      .finally(() => setLoading(false));
  }, [selectedCompanyId]);

  const handleJoin = (session: InventoryCountSession) => {
    if (!selectedCompanyId) return;
    navigate(`/dashboard/counting/method/${selectedCompanyId}/${session.id}`);
  };

  if (companyOptions.length === 0 && loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (companyOptions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" px={3}>
        <Alert severity="warning">No tienes ninguna empresa asignada. Contacta a tu administrador.</Alert>
      </Box>
    );
  }

  // Caso borde: el tomador esta asociado a mas de una empresa.
  if (companyOptions.length > 1 && !selectedCompanyId) {
    return (
      <Box display="flex" justifyContent="center" py={6} px={3}>
        <Card sx={{ maxWidth: 420, width: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Elige una empresa
            </Typography>
            <List>
              {companyOptions.map((c) => (
                <ListItem key={c.id} divider>
                  <ListItemText primary={c.name} />
                  <Button variant="outlined" onClick={() => setSelectedCompanyId(Number(c.id))}>
                    Elegir
                  </Button>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" px={3}>
      <Card sx={{ maxWidth: 480, width: '100%' }}>
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Toma de Inventario
          </Typography>

          {error && (
            <Alert severity="error" sx={{ my: 2, textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <CircularProgress sx={{ mt: 3 }} />
          ) : sessions.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              No hay ninguna toma de inventario activa en este momento. Cuando un administrador
              cargue el teórico y active una sesión, va a aparecer aquí.
            </Typography>
          ) : (
            <List sx={{ mt: 2, textAlign: 'left' }}>
              {sessions.map((session) => (
                <ListItem key={session.id} divider>
                  <ListItemText
                    primary={session.name}
                    secondary={`Bodega: ${session.warehouseName}`}
                  />
                  <Button variant="contained" onClick={() => handleJoin(session)}>
                    Comenzar
                  </Button>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ActiveSessionScreen;
